import React, { useState, useEffect } from 'react';
import { API, getToken } from './adminApi';

// Community sign-up pages (/groupsearch/join/:id): how far each visit got on
// the form — Google, the Facebook/Instagram browser sheet, typing, errors —
// so we can see where people give up. Uses the Page Analytics filters.

const STEP_LABELS = {
  email_focus: 'Tapped the email box',
  email_typed: 'Typed an email',
  password_focus: 'Tapped the password box',
  password_typed: 'Typed a password',
  terms_ticked: 'Ticked the terms box',
  terms_opened: 'Opened the Terms',
  privacy_opened: 'Opened the Privacy policy',
  submit: 'Pressed Sign up',
  submit_no_terms: 'Sign up blocked — terms not ticked',
  submit_error: 'Sign up error',
  google_click: 'Tapped Continue with Google',
  google_blocked: 'Tapped Google in the app browser — sheet shown',
  google_error: 'Google sign-in error',
  google_cancelled: 'Closed the Google pop-up',
  sheet_open_browser: 'Tapped "Open in browser"',
  sheet_steps: 'Shown the ••• menu steps',
  sheet_copy_link: 'Copied the link',
  sheet_email_instead: 'Chose email instead',
  account_created: 'Account created ✓',
};

const fmtTime = (d) => new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
const secs = (ms) => `${Math.round((ms || 0) / 1000)}s`;

const SignupAnalyticsSection = ({ days, communityId, includeInternal }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);

  useEffect(() => {
    (async () => {
      setError('');
      try {
        const token = await getToken();
        const qs = new URLSearchParams({ days, ...(communityId ? { communityId } : {}), ...(includeInternal ? { includeInternal: '1' } : {}) });
        const res = await fetch(`${API}/admin/signup-analytics?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
      } catch (err) {
        setError(`Couldn't load sign-up page analytics (${err.message})`);
      }
    })();
  }, [days, communityId, includeInternal]);

  const landed = data?.funnel[0]?.count || 0;

  return (
    <div style={{ marginTop: '2.5rem', paddingTop: '1.5rem', borderTop: '2px solid #e2e8f0' }}>
      <h2 className="as-section-title">Sign-up page</h2>
      <p className="as-pa-hint">
        Visits to a community&apos;s sign-up page (after tapping Join) and how far each got on the form. Uses the filters above.
        Tracking started 10 Oct 2026.
      </p>
      {error && <div className="as-error">{error}</div>}
      {!data && !error && <div className="as-loading">Loading…</div>}

      {data && (<>
        <div className="as-pa-split">
          <div className="as-pa-card">
            <h2 className="as-section-title">How far they got</h2>
            {data.funnel.map(f => (
              <div key={f.key} className="as-pa-hbar-row">
                <div className="as-pa-hbar-label">{f.label}</div>
                <div className="as-pa-hbar-track">
                  <div className="as-pa-hbar" style={{ width: `${landed ? (f.count / landed) * 100 : 0}%` }} />
                </div>
                <div className="as-pa-hbar-value">{f.count} <span>({landed ? Math.round((f.count / landed) * 100) : 0}%)</span></div>
              </div>
            ))}
          </div>

          <div className="as-pa-card">
            <h2 className="as-section-title">Problems &amp; choices</h2>
            <table className="as-act-table">
              <tbody>
                {data.events.map(e => <tr key={e.key}><td>{e.label}</td><td className="as-pa-num">{e.count}</td></tr>)}
              </tbody>
            </table>
            {data.errors.length > 0 && (<>
              <h2 className="as-section-title" style={{ marginTop: '1rem' }}>Error details</h2>
              <table className="as-act-table">
                <tbody>
                  {data.errors.map(e => <tr key={e.label}><td>{e.label}</td><td className="as-pa-num">{e.count}</td></tr>)}
                </tbody>
              </table>
            </>)}
          </div>
        </div>

        <div className="as-pa-split">
          <div className="as-pa-card">
            <h2 className="as-section-title">By browser</h2>
            <div className="as-act-table-wrap">
              <table className="as-act-table">
                <thead><tr><th></th><th>Visits</th><th>Touched form</th><th>Tapped Google</th><th>Tapped Facebook</th><th>Accounts</th></tr></thead>
                <tbody>
                  {data.byBrowser.length === 0 && <tr><td colSpan="6" className="as-act-empty">—</td></tr>}
                  {data.byBrowser.map(r => (
                    <tr key={r.key}><td>{r.key}</td><td className="as-pa-num">{r.visits}</td><td className="as-pa-num">{r.interacted}</td><td className="as-pa-num">{r.googleClicks}</td><td className="as-pa-num">{r.facebookClicks ?? 0}</td><td className="as-pa-num">{r.accounts}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {!communityId && (
            <div className="as-pa-card">
              <h2 className="as-section-title">By community</h2>
              <div className="as-act-table-wrap">
                <table className="as-act-table">
                  <thead><tr><th></th><th>Visits</th><th>Touched form</th><th>Accounts</th></tr></thead>
                  <tbody>
                    {data.byCommunity.length === 0 && <tr><td colSpan="4" className="as-act-empty">—</td></tr>}
                    {data.byCommunity.map(r => (
                      <tr key={r.key}><td>{r.key}</td><td className="as-pa-num">{r.visits}</td><td className="as-pa-num">{r.interacted}</td><td className="as-pa-num">{r.accounts}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <h2 className="as-section-title" style={{ marginTop: '1.5rem' }}>Visits ({data.recent.length} of {data.total})</h2>
        <div className="as-act-table-wrap">
          <table className="as-act-table">
            <thead><tr><th>Time</th><th>Community</th><th>Visitor</th><th>Browser</th><th>Time on</th><th>Got to</th></tr></thead>
            <tbody>
              {data.recent.length === 0 && <tr><td colSpan="6" className="as-act-empty">No sign-up page visits yet</td></tr>}
              {data.recent.map(v => (
                <React.Fragment key={v._id}>
                  <tr onClick={() => setOpen(open === v._id ? null : v._id)} style={{ cursor: 'pointer' }}>
                    <td>{fmtTime(v.createdAt)}</td>
                    <td>{v.community}</td>
                    <td>{v.visitorId.slice(0, 8)} · {v.device}</td>
                    <td>{v.browser}{v.iosVersion ? ` · iOS ${v.iosVersion}` : ''}</td>
                    <td className="as-pa-num">{secs(v.activeMs)}</td>
                    <td>
                      {v.accountCreated
                        ? <span className="as-pa-tag as-pa-tag--good">Account created</span>
                        : (v.steps.length ? STEP_LABELS[v.steps[v.steps.length - 1].step] || v.steps[v.steps.length - 1].step : 'Didn’t touch the form')}
                      {' '}{open === v._id ? '▲' : '▼'}
                    </td>
                  </tr>
                  {open === v._id && (
                    <tr>
                      <td colSpan="6">
                        <div className="as-pa-timeline">
                          <div>0:00 — landed on the sign-up page</div>
                          {v.steps.map((s, i) => (
                            <div key={i}>{secs(s.atMs)} — {STEP_LABELS[s.step] || s.step}{s.detail ? `: ${s.detail}` : ''}</div>
                          ))}
                          <div>{secs(v.activeMs)} — {v.accountCreated ? 'went on to set up their profile' : 'left'}</div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
  );
};

export default SignupAnalyticsSection;
